/**
 * Payment Methods Hook for ArendaPro+
 * 
 * Provides CRUD operations for payment methods using the new domain-specific table.
 * Replaces the generic tags system for payment method management.
 * 
 * Following STYLEGUIDE.md patterns:
 * - Uses supabaseWorker for data operations
 * - Proper error handling and loading states
 * - React Query for caching and synchronization
 * - TypeScript strict typing
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { useAuth } from '@/context/AuthContext';
import { 
  PaymentMethod, 
  PaymentMethodDb, 
  PaymentMethodInsert, 
  PaymentMethodUpdate,
  DomainStatusOption,
  DomainStatusFilters
} from '@/types/domain-statuses';
import {
  paymentMethodDbToClient,
  paymentMethodClientToDbInsert,
  paymentMethodUpdateToDb,
  statusesToOptions,
  validateStatusName,
  sanitizeStatusInput
} from '@/lib/mappers/domainStatusMappers';
import { toast } from 'sonner';

// =============================================================================
// QUERY KEYS
// =============================================================================

const PAYMENT_METHODS_QUERY_KEY = 'paymentMethods';

export const paymentMethodsKeys = {
  all: [PAYMENT_METHODS_QUERY_KEY] as const,
  lists: () => [...paymentMethodsKeys.all, 'list'] as const,
  list: (filters: DomainStatusFilters) => [...paymentMethodsKeys.lists(), filters] as const,
  details: () => [...paymentMethodsKeys.all, 'detail'] as const,
  detail: (id: string) => [...paymentMethodsKeys.details(), id] as const,
};

// =============================================================================
// MAIN HOOK
// =============================================================================

export interface UsePaymentMethodsOptions {
  filters?: DomainStatusFilters;
  enabled?: boolean;
  activeOnly?: boolean;
}

export function usePaymentMethods(options: UsePaymentMethodsOptions = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { filters = {}, enabled = true, activeOnly = false } = options;

  // =============================================================================
  // QUERIES
  // =============================================================================

  const {
    data: paymentMethods = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: paymentMethodsKeys.list({ ...filters, isActive: activeOnly }),
    queryFn: async (): Promise<PaymentMethod[]> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      let query = supabaseWorker
        .from('payment_methods')
        .select('*')
        .eq('user_id', user.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (filters.search) {
        query = query.ilike('name', `%${filters.search}%`);
      }

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching payment methods:', error);
        throw new Error(`Failed to fetch payment methods: ${error instanceof Error ? error instanceof Error ? error.message : String(error) : String(error)}`);
      }

      return (data as PaymentMethodDb[]).map(paymentMethodDbToClient);
    },
    enabled: enabled && !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // =============================================================================
  // MUTATIONS
  // =============================================================================

  const createMutation = useMutation({
    mutationFn: async (newMethod: Omit<PaymentMethod, 'id' | 'createdAt' | 'updatedAt' | 'userId'>): Promise<PaymentMethod> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Validate input
      const sanitized = sanitizeStatusInput(newMethod);
      
      if (!validateStatusName(sanitized.name || '')) {
        throw new Error('Название способа оплаты должно содержать от 1 до 100 символов');
      }

      // Check for duplicates
      const existingMethod = paymentMethods.find(
        method => method.name.toLowerCase() === sanitized.name?.toLowerCase()
      );
      
      if (existingMethod) {
        throw new Error('Способ оплаты с таким названием уже существует');
      }

      const insertData = paymentMethodClientToDbInsert({
        name: sanitized.name!,
        description: sanitized.description,
        isActive: newMethod.isActive ?? true,
        isDefault: newMethod.isDefault ?? false,
        config: newMethod.config ?? {},
        userId: user.id,
      });

      const { data, error } = await supabaseWorker
        .from('payment_methods')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Error creating payment method:', error);
        throw new Error(`Failed to create payment method: ${error instanceof Error ? error.message : String(error)}`);
      }

      return paymentMethodDbToClient(data as PaymentMethodDb);
    },
    onSuccess: (newMethod) => {
      // Invalidate and refetch payment methods
      queryClient.invalidateQueries({ queryKey: paymentMethodsKeys.all });
      toast.success(`Способ оплаты "${newMethod.name}" создан`);
    },
    onError: (error: Error) => {
      console.error('Error creating payment method:', error);
      toast.error(error instanceof Error ? error.message : String(error) || 'Ошибка при создании способа оплаты');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: PaymentMethodUpdate }): Promise<PaymentMethod> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Validate input
      const sanitized = sanitizeStatusInput(updates);

      if (sanitized.name && !validateStatusName(sanitized.name)) {
        throw new Error('Название способа оплаты должно содержать от 1 до 100 символов');
      }

      // Check for duplicates (excluding current method)
      if (sanitized.name) {
        const existingMethod = paymentMethods.find(
          method => method.id !== id && method.name.toLowerCase() === sanitized.name?.toLowerCase()
        );
        
        if (existingMethod) {
          throw new Error('Способ оплаты с таким названием уже существует');
        }
      }

      const updateData = paymentMethodUpdateToDb(updates);

      const { data, error } = await supabaseWorker
        .from('payment_methods')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating payment method:', error);
        throw new Error(`Failed to update payment method: ${error instanceof Error ? error.message : String(error)}`);
      }

      if (!data) {
        throw new Error('Payment method not found or access denied');
      }

      return paymentMethodDbToClient(data as PaymentMethodDb);
    },
    onSuccess: (updatedMethod) => {
      // Invalidate and refetch payment methods
      queryClient.invalidateQueries({ queryKey: paymentMethodsKeys.all });
      toast.success(`Способ оплаты "${updatedMethod.name}" обновлен`);
    },
    onError: (error: Error) => {
      console.error('Error updating payment method:', error);
      toast.error(error instanceof Error ? error.message : String(error) || 'Ошибка при обновлении способа оплаты');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabaseWorker
        .from('payment_methods')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting payment method:', error);
        throw new Error(`Failed to delete payment method: ${error instanceof Error ? error.message : String(error)}`);
      }
    },
    onSuccess: (_, deletedId) => {
      // Find the deleted method name for toast
      const deletedMethod = paymentMethods.find(method => method.id === deletedId);
      
      // Invalidate and refetch payment methods
      queryClient.invalidateQueries({ queryKey: paymentMethodsKeys.all });
      toast.success(`Способ оплаты "${deletedMethod?.name || ''}" удален`);
    },
    onError: (error: Error) => {
      console.error('Error deleting payment method:', error);
      toast.error(error instanceof Error ? error.message : String(error) || 'Ошибка при удалении способа оплаты');
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (items: Array<{ id: string; sortOrder: number }>): Promise<void> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Update each item's sort_order directly via Supabase
      const updates = items.map(item =>
        supabaseWorker
          .from('payment_methods')
          .update({ sort_order: item.sortOrder })
          .eq('id', item.id)
          .eq('user_id', user.id)
      );

      const results = await Promise.all(updates);
      
      // Check for errors
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        console.error('Error reordering payment methods:', errors);
        throw new Error('Failed to reorder some items');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentMethodsKeys.all });
      toast.success('Порядок обновлен');
    },
    onError: (error: Error) => {
      console.error('Error reordering payment methods:', error);
      toast.error(error instanceof Error ? error.message : String(error) || 'Ошибка при изменении порядка');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }): Promise<PaymentMethod> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabaseWorker
        .from('payment_methods')
        .update({ is_active: isActive })
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error toggling payment method status:', error);
        throw new Error(`Failed to toggle payment method status: ${error instanceof Error ? error.message : String(error)}`);
      }

      if (!data) {
        throw new Error('Payment method not found or access denied');
      }

      return paymentMethodDbToClient(data as PaymentMethodDb);
    },
    onSuccess: (updatedMethod) => {
      // Invalidate and refetch payment methods
      queryClient.invalidateQueries({ queryKey: paymentMethodsKeys.all });
      const statusText = updatedMethod.isActive ? 'активирован' : 'деактивирован';
      toast.success(`Способ оплаты "${updatedMethod.name}" ${statusText}`);
    },
    onError: (error: Error) => {
      console.error('Error toggling payment method status:', error);
      toast.error(error instanceof Error ? error.message : String(error) || 'Ошибка при изменении статуса способа оплаты');
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (id: string): Promise<PaymentMethod> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // First, unset all other defaults
      await supabaseWorker
        .from('payment_methods')
        .update({ is_default: false })
        .eq('user_id', user.id);

      // Then set the new default
      const { data, error } = await supabaseWorker
        .from('payment_methods')
        .update({ is_default: true })
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error setting default payment method:', error);
        throw new Error(`Failed to set default payment method: ${error instanceof Error ? error.message : String(error)}`);
      }

      if (!data) {
        throw new Error('Payment method not found or access denied');
      }

      return paymentMethodDbToClient(data as PaymentMethodDb);
    },
    onSuccess: (updatedMethod) => {
      // Invalidate and refetch payment methods
      queryClient.invalidateQueries({ queryKey: paymentMethodsKeys.all });
      toast.success(`"${updatedMethod.name}" установлен как способ оплаты по умолчанию`);
    },
    onError: (error: Error) => {
      console.error('Error setting default payment method:', error);
      toast.error(error instanceof Error ? error.message : String(error) || 'Ошибка при установке способа оплаты по умолчанию');
    },
  });

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  const allOptions: DomainStatusOption[] = statusesToOptions(paymentMethods);
  const activeOptions: DomainStatusOption[] = statusesToOptions(
    paymentMethods.filter(method => method.isActive)
  );

  const defaultMethod = paymentMethods.find(method => method.isDefault);
  const activeMethods = paymentMethods.filter(method => method.isActive);

  // =============================================================================
  // RETURN INTERFACE
  // =============================================================================

  return {
    // Data
    paymentMethods,
    activeMethods,
    options: allOptions,
    activeOptions,
    defaultMethod,
    
    // State
    isLoading,
    error,
    
    // Actions
    refetch,
    create: createMutation.mutate,
    update: updateMutation.mutate,
    delete: deleteMutation.mutate,
    reorder: reorderMutation.mutate,
    toggleActive: toggleActiveMutation.mutate,
    setDefault: setDefaultMutation.mutate,
    
    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isReordering: reorderMutation.isPending,
    isTogglingActive: toggleActiveMutation.isPending,
    isSettingDefault: setDefaultMutation.isPending,
    
    // Mutation errors
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    reorderError: reorderMutation.error,
    toggleActiveError: toggleActiveMutation.error,
    setDefaultError: setDefaultMutation.error,
  };
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook for getting payment method options for selectors
 */
export function usePaymentMethodOptions(activeOnly: boolean = true) {
  const { activeOptions, options: allOptions, isLoading, error } = usePaymentMethods({ 
    activeOnly 
  });
  
  return {
    options: activeOnly ? activeOptions : allOptions,
    isLoading,
    error,
  };
}

/**
 * Hook for getting a single payment method by ID
 */
export function usePaymentMethod(id: string | undefined) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: paymentMethodsKeys.detail(id || ''),
    queryFn: async (): Promise<PaymentMethod | null> => {
      if (!user?.id || !id) {
        return null;
      }

      const { data, error } = await supabaseWorker
        .from('payment_methods')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (error) {
        if (typeof error === 'object' && error !== null && 'code' in error && (error as { code: string }).code === 'PGRST116') {
          return null; // Not found
        }
        throw new Error(`Failed to fetch payment method: ${error instanceof Error ? error.message : String(error)}`);
      }

      return paymentMethodDbToClient(data as PaymentMethodDb);
    },
    enabled: !!user?.id && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
